import {
    MDBAccordion,
    MDBAccordionItem,
    MDBCol,
    MDBRow,
} from "mdb-react-ui-kit";
import Link from "next/link";
import { useRouter } from "next/router";
import useNavBarData from "@/utils/queries/hooks/useNavBarData";
import { useMemo } from "react";
import {
    footerSectionsOne,
    footerSectionsTwo,
} from "../../../utils/mockData/homeMocks";
import style from "../../../styles/Footer.module.scss";
import { randomUid } from "../../../utils/randomIds";

/* eslint-disable jsx-a11y/anchor-is-valid */
const TERMS_AND_CONDITIONS_PDF_URL =
    "https://d3t4iiwihaed0b.cloudfront.net/terms-condition/Terms_Conditions.pdf";
const TERMS_AND_CONDITIONS_LABEL = "Terms & Conditions";

const FooterLinksMobile = () => {
    const router = useRouter();
    const { data: dropDownData } = useNavBarData();

    const destinationList = useMemo(() => {
        const targetOrder = ["Roma", "Venice", "Florence", "Sorrento"] as const;
        const aliasToCanonical = new Map<string, string>([
            ["rome", "Rome"],
            ["roma", "Roma"],
            ["venezia", "Venice"],
            ["venice", "Venice"],
            ["firenze", "Florence"],
            ["florence", "Florence"],
            ["sorrento", "Sorrento"],
        ]);

        const toCanonical = (name: string): string => {
            const key = name?.trim().toLowerCase();
            return aliasToCanonical.get(key) ?? name;
        };

        const src = dropDownData?.toursByDestination ?? [];

        // Build map canonical -> first matching source item
        const canonicalToItem = new Map<string, (typeof src)[number]>();
        src.forEach(item => {
            const canonical = toCanonical(item.city);
            if (!canonicalToItem.has(canonical)) {
                canonicalToItem.set(canonical, item);
            }
        });

        // Return in the exact requested order with display label set
        return targetOrder
            .map(name => {
                const found = canonicalToItem.get(name);
                if (!found) return undefined;
                return { ...found, displayCity: name } as typeof found & {
                    displayCity: string;
                };
            })
            .filter(Boolean) as Array<
            (typeof src)[number] & { displayCity: string }
        >;
    }, [dropDownData]);

    const path = "/tours-destinations";

    const handleRouting = (tour: string | null, destination: string | null) => {
        const url = {
            pathname: path,
            query: {
                tourType: tour,
                destination,
            },
        };
        if (router.pathname !== path) {
            router.push(url);
        } else {
            router.replace(url).then(() => router.reload());
        }
    };

    const renderLink = (link: (typeof footerSectionsOne)[0]["links"][0]) => {
        if (link.title === TERMS_AND_CONDITIONS_LABEL) {
            return (
                <a
                    href={TERMS_AND_CONDITIONS_PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-reset"
                >
                    {link.title}
                </a>
            );
        }

        if (link.id === "Notice & collection") {
            return (
                <a href="#" className="iubenda-cs-uspr-link">
                    Notice at Collection
                </a>
            );
        }

        if (link.id === "Manage & consents") {
            return (
                <a href="#" className="iubenda-cs-preferences-link">
                    {link.title}
                </a>
            );
        }

        return (
            <Link
                href={link.href}
                className="text-reset"
                target={link.newPage ? "_blank" : ""}
            >
                {link.title}
            </Link>
        );
    };

    return (
        <MDBCol
            lg="3"
            xl="3"
            className={`mb-4 ${style.mobile} ${style.footerLinksMob}`}
        >
            <MDBRow className="p-0">
                {[...footerSectionsOne, ...footerSectionsTwo].map(section => (
                    <MDBCol size={12} className="mx-auto" key={section.id}>
                        <MDBAccordion
                            borderless
                            className={style.footerLinksMobAccordion}
                        >
                            <MDBAccordionItem
                                key={randomUid()}
                                collapseId={1}
                                headerTitle={section.title}
                                className={`${style.accordionItem} ${style.accordionItemMobile} border-1`}
                            >
                                {section.id === "Tours" &&
                                    dropDownData?.toursHome?.map(link => (
                                        <p
                                            className={`${style.text_content} text-start`}
                                            key={link.tourType}
                                        >
                                            <span
                                                onClick={() =>
                                                    handleRouting(
                                                        link.type,
                                                        null
                                                    )
                                                }
                                                className="text-reset"
                                            >
                                                {link.tourType}
                                            </span>
                                        </p>
                                    ))}
                                {section.id === "Destinations" &&
                                    (destinationList &&
                                    destinationList.length > 0
                                        ? destinationList
                                        : ([
                                              { city: "Rome" },
                                              { city: "Venice" },
                                              { city: "Florence" },
                                              { city: "Sorrento" },
                                          ] as Array<{
                                              city: string;
                                              displayCity?: string;
                                          }>)
                                    ).map(
                                        (link: {
                                            city: string;
                                            displayCity?: string;
                                        }) => (
                                            <p
                                                className={`${style.text_content} text-start`}
                                                key={link.city}
                                            >
                                                <span
                                                    onClick={() =>
                                                        handleRouting(
                                                            null,
                                                            link.displayCity ??
                                                                link.city
                                                        )
                                                    }
                                                    className="text-reset"
                                                >
                                                    {link.displayCity ??
                                                        link.city}
                                                </span>
                                            </p>
                                        )
                                    )}
                                {section.id !== "Tours" &&
                                    section.id !== "Destinations" &&
                                    section.links.map(link => (
                                        <p
                                            className={`${style.text_content} text-start`}
                                            key={link.id}
                                        >
                                            {renderLink(link)}
                                        </p>
                                    ))}
                            </MDBAccordionItem>
                        </MDBAccordion>
                    </MDBCol>
                ))}
            </MDBRow>
        </MDBCol>
    );
};
export default FooterLinksMobile;
